import { useEffect, useState } from "react";
import api, { money, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Wallet, TrendUp, TrendDown, Warning, ChartLineUp, DownloadSimple, FilePdf, SignOut, Receipt, ArrowsLeftRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

function Stat({ label, value, sub, icon: Icon, danger }) {
  return (
    <div className="border-r border-b border-border p-6 flex flex-col justify-between min-h-[130px]">
      <div className="flex items-center justify-between">
        <p className="label-caps">{label}</p>
        <Icon size={18} className={danger ? "text-destructive" : "text-muted-foreground"} />
      </div>
      <div>
        <div className={`font-mono-fig text-3xl font-semibold tabular-nums ${danger ? "text-destructive" : ""}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [logo, setLogo] = useState(null);
  const [cats, setCats] = useState([]);
  const [tax, setTax] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data.filter((c) => c.item_count > 0))).catch(() => {});
    api.get("/team/branding").then((r) => { if (r.data.has_logo) setLogo(`${API}/team/branding/logo`); }).catch(() => {});
    api.get("/invoices/tax-summary").then((r) => setTax(r.data)).catch(() => {});
  }, []);

  const download = async (path, filename) => {
    try {
      const fmt = filename.endsWith("pdf") ? "pdf" : "csv";
      const res = await api.get(path, { responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "text/csv";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download report"); }
  };

  const spentPct = data && data.total_budget > 0 ? Math.round((data.total_actual / data.total_budget) * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b-2 border-[#0A0A0A] flex items-center justify-between px-4 sm:px-8 sticky top-0 bg-white z-40">
        <div className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt="Company" className="max-h-9 max-w-[150px] object-contain" data-testid="portal-logo" />
          ) : (
            <span className="font-heading font-black text-lg tracking-tighter">JNIA-BuildTrack</span>
          )}
          <span className="hidden sm:inline text-xs uppercase tracking-widest text-muted-foreground border-l border-border pl-3">Client Interphase</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-sm text-muted-foreground" data-testid="portal-user">{user?.name}</span>
          <Button variant="outline" size="sm" onClick={logout} className="rounded-none" data-testid="portal-logout">
            <SignOut size={16} className="mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <div className="p-4 sm:p-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="label-caps">Progress Overview</p>
            <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter">Project Dashboard</h1>
          </div>
          <div className="flex flex-wrap gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-none" data-testid="portal-portfolio-report">
                <DownloadSimple size={16} className="mr-1" /> Portfolio report
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none w-52">
              <DropdownMenuLabel className="label-caps text-xs">Full cost report</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => download("/reports/portfolio-cost-report.pdf", "portfolio_cost_report.pdf")} data-testid="portal-portfolio-pdf" className="cursor-pointer">
                <FilePdf size={16} className="mr-2" /> Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("/reports/portfolio-cost-report.csv", "portfolio_cost_report.csv")} data-testid="portal-portfolio-csv" className="cursor-pointer">
                <DownloadSimple size={16} className="mr-2" /> Download CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none" data-testid="portal-tax-report">
                <Receipt size={16} className="mr-1" /> Tax report
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none w-52">
              <DropdownMenuLabel className="label-caps text-xs">Taxes paid (all invoices)</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => download("/reports/tax-report.pdf", "taxes_paid_report.pdf")} data-testid="portal-tax-pdf" className="cursor-pointer">
                <FilePdf size={16} className="mr-2" /> Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("/reports/tax-report.csv", "taxes_paid_report.csv")} data-testid="portal-tax-csv" className="cursor-pointer">
                <DownloadSimple size={16} className="mr-2" /> Download CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>

        {!data ? (
          <div className="label-caps animate-pulse">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border mb-8">
              <Stat label="Total Budget" value={money(data.total_budget)} sub={`${data.project_count} projects`} icon={Wallet} />
              <Stat label="Total Spent" value={money(data.total_actual)} sub={`${spentPct}% of budget`} icon={TrendUp} />
              <Stat label="Remaining" value={money(data.remaining)} sub={data.remaining < 0 ? "Over budget" : "Under budget"} icon={TrendDown} danger={data.remaining < 0} />
              <Stat label="Over Budget" value={data.over_budget_count} sub="projects flagged" icon={Warning} danger={data.over_budget_count > 0} />
            </div>

            {tax && tax.invoice_count > 0 && (
              <div className="border border-border mb-8 grid grid-cols-1 sm:grid-cols-3" data-testid="portal-taxes-paid-card">
                <div className="p-6 border-b sm:border-b-0 sm:border-r border-border flex items-center gap-3 bg-secondary">
                  <Receipt size={20} weight="bold" />
                  <div>
                    <p className="label-caps">Taxes Paid to Date</p>
                    <div className="font-mono-fig text-3xl font-semibold tabular-nums" data-testid="portal-taxes-paid-total">{money(tax.total_tax)}</div>
                  </div>
                </div>
                <div className="p-6 border-b sm:border-b-0 sm:border-r border-border">
                  <p className="label-caps mb-2">Total Invoiced</p>
                  <div className="font-mono-fig text-2xl font-semibold tabular-nums">{money(tax.total_invoiced)}</div>
                </div>
                <div className="p-6">
                  <p className="label-caps mb-2">Invoices Read</p>
                  <div className="font-mono-fig text-2xl font-semibold tabular-nums">{tax.invoice_count}</div>
                  <div className="text-xs text-muted-foreground mt-1">AI-extracted sales/GST tax</div>
                </div>
              </div>
            )}

            {data.portfolio?.projects_analyzed > 0 && (
              <div className="border border-border mb-8" data-testid="portal-portfolio-profit">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary">
                  <ChartLineUp size={18} weight="bold" />
                  <span className="font-heading font-bold">Portfolio Profit — If Sold</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
                  <div className="p-4"><p className="label-caps mb-1">Projected Sales</p><p className="font-mono-fig text-lg">{money(data.portfolio.total_sale_value)}</p></div>
                  <div className="p-4"><p className="label-caps mb-1">Total Costs</p><p className="font-mono-fig text-lg">{money(data.portfolio.total_build_cost + data.portfolio.total_selling_costs)}</p></div>
                  <div className="p-4"><p className="label-caps mb-1">Net Profit</p><p className={`font-mono-fig text-lg ${data.portfolio.total_net_profit >= 0 ? "text-emerald-700" : "text-destructive"}`}>{money(data.portfolio.total_net_profit)}</p></div>
                  <div className="p-4"><p className="label-caps mb-1">Avg Margin</p><p className="font-mono-fig text-lg">{data.portfolio.avg_margin.toFixed(1)}%</p></div>
                </div>
              </div>
            )}

            {data.over_budget_projects?.length > 0 && (
              <div className="border border-destructive mb-8" data-testid="portal-variance">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-destructive bg-destructive/5">
                  <Warning size={18} weight="bold" className="text-destructive" />
                  <span className="font-semibold text-destructive text-sm">Variance Alerts</span>
                </div>
                <div className="divide-y divide-border">
                  {data.over_budget_projects.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="font-medium">{p.name}</span>
                      <span className="font-mono-fig text-destructive tabular-nums">+{money(p.over_by)} over</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.allocation_drift?.length > 0 && (
              <div className="border border-indigo-400 mb-8" data-testid="portal-allocation-drift">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-indigo-400 bg-indigo-50">
                  <ArrowsLeftRight size={18} weight="bold" className="text-indigo-600" />
                  <span className="font-semibold text-indigo-700 text-sm">Allocation Drift — vs typical industry range</span>
                </div>
                <div className="divide-y divide-border">
                  {data.allocation_drift.map((d) => {
                    const over = d.status === "over";
                    const c = over ? "text-rose-600" : "text-blue-600";
                    const key = `${d.project_id}-${d.category.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
                    return (
                      <div key={key} data-testid={`portal-drift-${key}`} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium block truncate">{d.category}</span>
                          <span className="text-xs text-muted-foreground">{d.project_name} · {d.group} · <span className="font-mono-fig">{money(d.amount)}</span> spent</span>
                        </div>
                        <div className="text-right shrink-0">
                          <span className={`font-mono-fig tabular-nums font-semibold ${c}`}>{d.pct}%</span>
                          <span className="block text-[11px] text-muted-foreground">typical {d.typical_min}–{d.typical_max}%</span>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 border shrink-0 ${over ? "border-rose-400 text-rose-600" : "border-blue-400 text-blue-600"}`}>
                          {over ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                          {over ? "Over" : "Under"} +{d.drift}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="border border-border p-6 mb-8">
              <p className="label-caps mb-6">Budget vs Actual — by Project</p>
              {data.by_project.length === 0 ? (
                <p className="text-sm text-muted-foreground">No project data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={data.by_project} margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 0, border: "1px solid #0A0A0A" }} />
                    <Legend />
                    <Bar dataKey="budget" name="Budget" fill="#94a3b8" />
                    <Bar dataKey="actual" name="Actual" fill="#0A0A0A" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {cats.length > 0 && (
              <div className="border border-border" data-testid="portal-category-reports">
                <div className="px-4 py-3 border-b border-border bg-secondary"><span className="font-heading font-bold">Cost Reports by Category</span></div>
                <div className="divide-y divide-border">
                  {cats.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <span className="text-sm font-medium">{c.name}</span>
                        <span className="block text-xs text-muted-foreground">{c.item_count} items · {money(c.total_budget)} budgeted · {money(c.total_actual)} actual</span>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => download(`/categories/${c.id}/export.pdf`, `${c.name}_cost_report.pdf`)} className="rounded-none" data-testid={`portal-cat-pdf-${c.id}`}><FilePdf size={14} className="mr-1" /> PDF</Button>
                        <Button variant="outline" size="sm" onClick={() => download(`/categories/${c.id}/export.csv`, `${c.name}_cost_report.csv`)} className="rounded-none" data-testid={`portal-cat-csv-${c.id}`}><DownloadSimple size={14} className="mr-1" /> CSV</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
